import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { getErrorResponse, getTimeRelativeTime, getUpdatedSearchParams, parseSearchParams } from '../../../../services/util'
import { twElementStyles } from '../../../../twElementStyles'
import { usePopup } from '../../../../context/popupContext'
import { RenderDefaultActions, RenderStatus } from '../../common'
import { CustomTable } from '../../../../components/customTable'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import { useNotificationBar } from '../../../../context/notificationContext'
import { SmoothTransition } from '../../../../components/pageTransition'
import { Unauthorized } from '../../../Unauthorized'
import { PagePreloader } from '../../../../components/pagePreloader'
import { useNavigate } from 'react-router-dom';
import { useDeletePages, useGetPagesData, useStatusPages, useUpdatePages } from 'hooks/pages'

export interface Response {
    records: any[],
    pages?: number,
    total?: number,
    pagination?: Object
}
export const PagesListing = () => {
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')
    const navigate = useNavigate();
    const notification = useNotificationBar()
    let popup = usePopup()

    let { data, isFetching: isLoading, refetch, isError, isSuccess, error, dataUpdatedAt } = useGetPagesData(filterParams, {
        keepPreviousData: true
    })
    let { mutate: updateStatusPages } = useStatusPages()
    let { mutate: updatePages } = useUpdatePages()
    let { mutate: deletePages } = useDeletePages()

    let useFormHook = useForm({
        defaultValues: filterParams
    })

    let responseData: any = data?.data;
    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure ?',
            message: `Do you want to delete Page ${row?.title} ? This cannot be undone.`,
            type: 'confirm',
            onAction: (value: boolean) => {
                if (value) {
                    deletePages({
                        id: row?.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `Page deleted !`,
                                message: `Page ${row?.title} has been deleted successfully.`,
                                type: 'info'
                            })
                            refetch();
                        },
                        onError(error) {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed
        })
    }

    const onStatusChange = async (row: any) => {
        let update: any = { status: row.status ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.status ? 'Activate' : 'Deactivate'} Page ${row.title} ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updatePages({
                        data: update,
                        id: row.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `${update.status ? 'activation success !' : 'deactivated !'} `,
                                message: `${row.title} ${update.status ? 'activated' : 'deactivated'} successfully !`,
                                type: update.status ? 'success' : 'info'
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            }
        })

    }

    const onAction = (actionType: any, row: any) => {
        if (actionType === 'edit') {
            navigate(`update-page/${row.id}`);
        }
        if (actionType === 'delete') onDeleteAction(row)
    }

    const renderWidgetChips = ({ row }: any) => {
        const sections = row.original.page_data?.sections ?? [];

        if (!sections.length) {
            return <span className="text-gray-400">—</span>;
        }

        const MAX_VISIBLE = 3;
        const visible = sections.slice(0, MAX_VISIBLE);
        const hidden = sections.slice(MAX_VISIBLE);

        return (
            <div className="flex flex-wrap gap-1 max-w-[220px] relative">
                {visible.map((w: any, index: number) => (
                    <span
                        key={index}
                        className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 whitespace-nowrap"
                    >
                        {w.widget_name}
                    </span>
                ))}

                {hidden.length > 0 && (
                    <div className="relative group">
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 cursor-default">
                            +{hidden.length} more
                        </span>

                        {/* Hover popup */}
                        <div
                            className="
                    absolute left-0 top-full mt-1
                    hidden group-hover:block
                    z-50
                    min-w-[180px]
                    max-w-[260px]
                    p-2
                    bg-white
                    border
                    rounded
                    shadow-lg
                  "
                        >
                            <div className="flex flex-wrap gap-1">
                                {hidden.map((w: any, index: number) => (
                                    <span
                                        key={index}
                                        className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 whitespace-nowrap"
                                    >
                                        {w.widget_name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };


    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'title',
            header: () => 'PAGE NAME',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'description',
            header: 'DESCRIPTION',
        },
        {
            id: 'widgets_name',
            header: 'WIDGETS',
            cell: renderWidgetChips,
        },
        {
            id: 'status',
            header: 'STATUS',
            accessorKey: 'status',
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
            enableSorting: false
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction)
        },
    ]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'name'
        }
    }

    const rowSelectionState = useState<RowSelectionState>({})

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                }
                else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [{
        key: 'is_active',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Active`,
                value: 1
            },
            {
                display: `Inactive`,
                value: 0
            }
        ],
        label: 'Status'
    },
    ]

    useEffect(() => {
        const handlePrintScreen = (e) => {
            if (e.key === 'PrintScreen') {
                navigator.clipboard.writeText('');
                alert('Screenshots disabled!');
            }
        };

        const handleCtrlP = (e) => {
            if (e.ctrlKey && e.key === 'p') {
                alert('This section is not allowed to print or export to PDF');
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('keyup', handlePrintScreen);
        document.addEventListener('keydown', handleCtrlP);

        return () => {
            document.removeEventListener('keyup', handlePrintScreen);
            document.removeEventListener('keydown', handleCtrlP);
        };
    }, []);

    return (
        (isError || isSuccess) ?
            <SmoothTransition>
                {
                    (error as any)?.response?.data?.statusCode === 403 ?
                        <Unauthorized /> :
                        <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                            <aside className="flex flex-auto items-center">
                                <CustomTable
                                    title='Pages Management'
                                    titleLabel={(!isError) ? `Last fetched on ${getTimeRelativeTime(dataUpdatedAt)}` : ''}
                                    formHook={useFormHook}
                                    columns={columns}
                                    data={responseData}
                                    pages={responseData?.pages}
                                    totalRecords={responseData?.total}
                                    onRefresh={refetch}
                                    isLoading={isLoading}
                                    isErrored={isError}
                                    rowSelectionState={rowSelectionState}
                                    mobileViewOptions={mobileViewOptions}
                                    useFieldForSelect='id'
                                    filterOptions={filterOptions}
                                    onAddAction={() => {
                                        navigate('/pages/page-management/create-page');
                                    }}
                                />
                            </aside>
                        </div>
                }
            </SmoothTransition>
            : <PagePreloader />
    )
}
